import { Grid as GridBase, Input as InputBase, Text as TextBase } from '@chakra-ui/react';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiClock, FiRefreshCw } from 'react-icons/fi';
import {
  INVESTIGACAO_URL,
  PAINEL_URL,
  rotuloDaEmpresa,
  rotuloDaFilial,
  TIPOS_DE_GRAFICO,
  useAssistente,
} from '../../context/AssistenteContext';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeModeContext';
import { rotuloRegiao, tabelaRegioes } from '../../helpers/regioes';
import { useFiltroDaSessao } from '../../hooks/useFiltroDaSessao';
import { hojeIso } from '../../helpers/dataBr';
import api from '../../service/api';
import { AbaComparacao } from './painel/AbaComparacao';
import { AbaPendencias } from './painel/AbaHoje';
import { AbasDoPainel, DefinicaoDaAba, idDaAba, idDoConteudo, NomeDaAba } from './painel/AbasDoPainel';
import { Aviso, Botao, Box, CartaoComTitulo, Esqueleto, Etiqueta, Faixa, Flex, Grade } from './painel/base';
import { BarrasHorizontais, Colunas } from './painel/Visuais';
import { curto, dataHora, primeiroDiaUtilDoMesAnterior, quantidade } from './painel/formato';
import { ConteudoDaGaveta, Gaveta, PedidoDeInvestigacao } from './painel/Gaveta';
import { Alerta, Analise, DadosDoPainel, Investigacao } from './painel/tipos';
import { CORES_CLARO, CORES_ESCURO, variaveisDoTema } from './visual';

// Cast Chakra components to bypass TS2590 ("union type too complex").
const Grid = GridBase as React.ComponentType<any>;
const Input = InputBase as React.ComponentType<any>;
const Text = TextBase as React.ComponentType<any>;

// O painel abre do 1º dia útil do mês anterior até ontem: as pendências seguem
// o período, e começar no dia 1º deste mês escondia os pedidos sem nota de
// outubro (o dono, 04/11/2026). Até ontem, para não misturar um dia incompleto.
const hojePadrao = hojeIso();
const ontemPadraoData = new Date(`${hojePadrao}T12:00:00`);
ontemPadraoData.setDate(ontemPadraoData.getDate() - 1);
const fimPadrao = `${ontemPadraoData.getFullYear()}-${String(ontemPadraoData.getMonth() + 1).padStart(2, '0')}-${String(ontemPadraoData.getDate()).padStart(2, '0')}`;
const inicioPadrao = primeiroDiaUtilDoMesAnterior(hojePadrao);

function deslocarMes(iso: string, meses: number, manterFimDoMes = false) {
  const [ano, mes, dia] = iso.split('-').map(Number);
  const ultimoDiaOrigem = new Date(ano, mes, 0, 12).getDate();
  const destino = new Date(ano, mes - 1 - meses, 1, 12);
  const ultimoDiaDestino = new Date(destino.getFullYear(), destino.getMonth() + 1, 0, 12).getDate();
  const diaDestino = manterFimDoMes && dia === ultimoDiaOrigem
    ? ultimoDiaDestino
    : Math.min(dia, ultimoDiaDestino);
  return `${destino.getFullYear()}-${String(destino.getMonth() + 1).padStart(2, '0')}-${String(diaDestino).padStart(2, '0')}`;
}

function periodoAnterior(inicio: string, fim: string) {
  if (!inicio || !fim || inicio > fim) return null;
  let meses = 1;
  while (deslocarMes(fim, meses, true) >= inicio) meses += 1;
  return {
    inicio: deslocarMes(inicio, meses),
    fim: deslocarMes(fim, meses, true),
  };
}

// O painel de inteligência comercial da página do assistente, com a situação
// de hoje e uma análise do intervalo escolhido no calendário. Os números, a classificação e as
// recomendações vêm prontos do MCP (suplen_mcp/painel.py), com o que quem está
// logado pode ver: o vendedor, só o dele. Esta tela só desenha.
//
// Cada alerta abre uma gaveta com o detalhe; o de variação da venda traz a
// investigação (onde está a diferença), nos períodos exatos da análise, pela
// rota /api/assistente/investigacao do mesmo MCP — sem passar pela IA.

function Carregando() {
  return (
    <Box aria-busy="true" aria-label="Montando o diagnóstico">
      <Flex sx={{ gap: '8px' }} mb="20px">
        {[180, 170, 200].map((w) => (
          <Esqueleto key={w} h="42px" w={`${w}px`} maxW="30%" borderRadius="999px" />
        ))}
      </Flex>
      <Faixa mb="16px">
        {[0, 1, 2, 3].map((i) => (
          <Esqueleto key={i} h="112px" />
        ))}
      </Faixa>
      <Grade>
        <Esqueleto gridColumn={{ lg: 'span 8' }} h="320px" />
        <Esqueleto gridColumn={{ lg: 'span 4' }} h="320px" />
      </Grade>
      <Text fontSize="13px" color="var(--pa-tinta-3)" mt="16px" textAlign="center">
        A primeira carga lê os pedidos de todas as filiais e leva alguns segundos.
      </Text>
    </Box>
  );
}

function GraficoDeCirurgias({
  titulo,
  contagens,
  totalGeral,
  horizontal = false,
  gridColumn,
}: {
  titulo: string;
  contagens?: Record<string, number>;
  totalGeral: number;
  horizontal?: boolean;
  gridColumn?: Record<string, string>;
}) {
  const partes = Object.entries(contagens || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, horizontal ? 12 : 8)
    .map(([nome, valor]) => ({ nome, valor, texto: String(valor) }));
  return (
    <CartaoComTitulo
      titulo={titulo}
      legenda="Quantidade de cirurgias no período."
      gridColumn={gridColumn || { base: '1 / -1', lg: 'span 6' }}
    >
      {partes.length ? (
        horizontal ? (
          <BarrasHorizontais
            rotuloDoGrafico={titulo}
            linhas={partes.map(({ nome, valor }) => ({
              chave: nome,
              rotulo: nome,
              segmentos: [{ nome: 'Cirurgias', valor, cor: 'var(--pa-dado)' }],
              texto: `${quantidade(valor)} · ${totalGeral ? Math.round((valor / totalGeral) * 100) : 0}%`,
              dica: nome,
            }))}
          />
        ) : (
          <Colunas partes={partes} altura={112} />
        )
      ) : (
        <Text fontSize="13px" color="var(--pa-tinta-3)">Sem cirurgias nesse recorte.</Text>
      )}
    </CartaoComTitulo>
  );
}

function GraficosDeCirurgias({ dados }: { dados?: DadosDoPainel['cirurgias'] }) {
  if (!dados) {
    return <Aviso tom="atencao">Os gráficos de cirurgia não vieram do servidor. Reinicie o MCP e atualize o painel.</Aviso>;
  }
  if (!dados.disponivel) {
    return (
      <Aviso tom="atencao">
        {dados.mensagem || 'Não foi possível carregar as cirurgias.'}
      </Aviso>
    );
  }
  const resumo = dados.resumo || {};
  const cruzamentos = resumo.por_medico_convenio_tipo || [];
  const medicos = Object.keys(resumo.por_medico || {}).length;
  const convenios = Object.keys(resumo.por_convenio || {}).length;
  const tipos = Object.keys(resumo.por_tipo_cirurgia || {}).length;
  const totalCirurgias = resumo.cirurgias || 0;
  const indicadores = [
    { rotulo: 'Cirurgias no período', valor: quantidade(totalCirurgias), detalhe: 'registros analisados' },
    { rotulo: 'Médicos', valor: quantidade(medicos), detalhe: 'com cirurgia no período' },
    { rotulo: 'Convênios', valor: quantidade(convenios), detalhe: 'com atendimento no período' },
    { rotulo: 'Tipos de cirurgia', valor: quantidade(tipos), detalhe: 'identificados no período' },
  ];
  return (
    <Box as="section" aria-label="Cirurgias por médico, convênio e tipo">
      <Flex justify="space-between" align="flex-end" wrap="wrap" sx={{ gap: '8px 16px' }} mb="12px">
        <Box>
          <Text as="h2" m={0} fontSize="18px" fontWeight={700}>Cirurgias por médico</Text>
          <Text fontSize="13px" color="var(--pa-tinta-3)" mt="2px">
            {dados.periodo} · hoje não incluído · {resumo.cirurgias || 0} cirurgias
          </Text>
        </Box>
      </Flex>
      <Box
        display="grid"
        gridTemplateColumns={{ base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
        gap="10px"
        mb="14px"
      >
        {indicadores.map((item) => (
          <Box
            key={item.rotulo}
            minW={0}
            p={{ base: '12px', md: '14px 16px' }}
            bg="var(--pa-superficie)"
            border="1px solid var(--pa-linha)"
            borderRadius="12px"
          >
            <Text fontSize="11px" fontWeight={600} color="var(--pa-tinta-3)">{item.rotulo}</Text>
            <Text fontSize={{ base: '20px', md: '24px' }} lineHeight="1.2" fontWeight={700} color="var(--pa-tinta)" mt="5px">
              {item.valor}
            </Text>
            <Text fontSize="11px" color="var(--pa-tinta-3)" mt="3px">{item.detalhe}</Text>
          </Box>
        ))}
      </Box>
      <Grade>
        <GraficoDeCirurgias
          titulo="Por médico · nomes completos · top 12"
          contagens={resumo.por_medico}
          totalGeral={totalCirurgias}
          horizontal
          gridColumn={{ base: '1 / -1', lg: 'span 12' }}
        />
        <GraficoDeCirurgias
          titulo="Por convênio · top 8"
          contagens={resumo.por_convenio}
          totalGeral={totalCirurgias}
          gridColumn={{ base: '1 / -1', lg: 'span 12' }}
        />
      </Grade>
      {!!cruzamentos.length && (
        <Box mt="16px" p="14px" bg="var(--pa-superficie)" border="1px solid var(--pa-linha)" borderRadius="12px">
          <Text as="h3" m={0} mb="8px" fontSize="14px" fontWeight={700}>
            Médico × convênio × tipo de cirurgia
          </Text>
          <Box as="table" w="100%" fontSize="13px" sx={{ borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Médico', 'Convênio', 'Tipo de cirurgia', 'Cirurgias'].map((nome) => (
                <Box key={nome} as="th" textAlign={nome === 'Cirurgias' ? 'right' : 'left'} py="6px" color="var(--pa-tinta-3)">{nome}</Box>
              ))}
            </tr></thead>
            <tbody>{cruzamentos.map((linha, i) => (
              <tr key={`${linha.medico}-${linha.convenio}-${linha.tipo_cirurgia}-${i}`}>
                <Box as="td" py="6px" borderTop="1px solid var(--pa-linha)">{linha.medico}</Box>
                <Box as="td" py="6px" borderTop="1px solid var(--pa-linha)">{linha.convenio}</Box>
                <Box as="td" py="6px" borderTop="1px solid var(--pa-linha)">{linha.tipo_cirurgia}</Box>
                <Box as="td" py="6px" textAlign="right" borderTop="1px solid var(--pa-linha)">{linha.cirurgias}</Box>
              </tr>
            ))}</tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Antes do relatório, a empresa: a Suplen tem duas (Suplen Medical e
// NeuroVasc). O painel dela soma as filiais; para o detalhe de uma filial, o
// seletor do alto da página. Nada é consultado ao MCP até a escolha.
function EscolhaDaEmpresa() {
  const { empresas, carregandoFiliais, erroFiliais, recarregarFiliais, escolherEmpresa } =
    useAssistente();
  if (carregandoFiliais && !empresas.length) return <Carregando />;
  if (!empresas.length) {
    return (
      <Box textAlign="center" pt="10">
        <Text color="var(--pa-tinta-2)" mb="4">
          {erroFiliais
            ? 'Não foi possível carregar as empresas.'
            : 'Nenhuma empresa ativa encontrada.'}
        </Text>
        <Botao leftIcon={<FiRefreshCw />} onClick={recarregarFiliais}>
          Tentar de novo
        </Botao>
      </Box>
    );
  }
  return (
    <Box maxW="760px" mx="auto" pt={['2', '8']}>
      <Text fontSize="18px" fontWeight={600} mb="1">
        Escolha a empresa
      </Text>
      <Text color="var(--pa-tinta-2)" mb="5">
        O painel mostra a análise comercial da empresa, com todas as filiais
        somadas. Para ver uma filial, escolha-a depois no alto da página.
      </Text>
      <Grid templateColumns={['1fr', 'repeat(2, 1fr)']} gap="10px">
        {empresas.map((e) => (
          <Botao
            key={e.codigo}
            h="auto"
            py="14px"
            flexDirection="column"
            alignItems="flex-start"
            textAlign="left"
            whiteSpace="normal"
            onClick={() => escolherEmpresa(e.codigo)}
          >
            <Text as="span" fontSize="14px">
              {rotuloDaEmpresa(e)}
            </Text>
            <Text as="span" fontSize="12px" fontWeight={400} color="var(--pa-tinta-3)" mt="2px">
              {e.filiais.length === 1
                ? rotuloDaFilial(e.filiais[0])
                : `${e.filiais.length} filiais: ${e.filiais.map((f) => f.codigo).join(', ')}`}
            </Text>
          </Botao>
        ))}
      </Grid>
    </Box>
  );
}

function legendaDaAnalise(a?: Analise): string {
  if (!a) return '';
  if (!a.disponivel) return a.primeira_comparacao_em ? `a partir de ${a.primeira_comparacao_em}` : 'sem histórico';
  const { atual, anterior } = a.periodo;
  if (!atual) return '';
  return anterior ? `${curto(atual)} contra ${curto(anterior)}` : `${curto(atual)} · sem comparação`;
}

export function PainelInteligencia({ onPerguntar }: { onPerguntar: (texto: string) => void }) {
  const { user } = useAuth();
  const { empresaCodigo, filialId, regiao, empresas, filiais, escolherRegiao } = useAssistente();
  const { ehClaro } = useThemeMode();
  const cores = ehClaro ? CORES_CLARO : CORES_ESCURO;
  const [painel, setPainel] = useState<DadosDoPainel | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [datasDoFormulario, setDatasDoFormulario] = useState({ inicio: inicioPadrao, fim: fimPadrao });
  const [periodoEscolhido, setPeriodoEscolhido] = useState({ inicio: inicioPadrao, fim: fimPadrao });
  const [erroPeriodo, setErroPeriodo] = useState('');
  const comparacaoDoFormulario = periodoAnterior(datasDoFormulario.inicio, datasDoFormulario.fim);
  // A aba fica ao trocar de empresa, filial ou região, e ao voltar à página.
  const [aba, setAba] = useFiltroDaSessao<NomeDaAba>(`@Aplication:assistente:aba:v3:${user?.id || ''}`, 'personalizado');
  const [gaveta, setGaveta] = useState<ConteudoDaGaveta | null>(null);
  // Cada carga tem um número: a resposta de uma empresa ou filial que já foi
  // trocada chega depois e é descartada, para não aparecer como a da nova.
  const rodada = useRef(0);
  // As investigações já feitas deste painel: abrir de novo não consulta de novo.
  const investigacoes = useRef(new Map<string, Promise<Investigacao>>());

  // `atualizar`: o botão "Atualizar" pede ao MCP os pedidos de agora no ESF,
  // não os do cache de 5 minutos. Abrir a página e trocar o filtro usam o cache.
  const carregar = useCallback(async (atualizar = false) => {
    const minha = ++rodada.current;
    if (!empresaCodigo) return;
    if (!PAINEL_URL) {
      setPainel({ disponivel: false, mensagem: 'O painel não está disponível neste ambiente.' });
      return;
    }
    setCarregando(true);
    try {
      // O mesmo login (Bearer) do chat: o MCP confere com o ESF quem é.
      const resposta = await axios.post(
        PAINEL_URL,
        {
          // A empresa (todas as filiais somadas) ou, se escolhida, a filial.
          empresa_codigo: empresaCodigo,
          empresa_id: filialId || undefined,
          regiao: regiao || undefined,
          usuario_id: user?.id,
          graficos: TIPOS_DE_GRAFICO,
          inicio: periodoEscolhido.inicio,
          fim: periodoEscolhido.fim,
          atualizar: atualizar || undefined,
        },
        {
          headers: { Authorization: api.defaults.headers.common['Authorization'] },
          timeout: 120000,
        },
      );
      if (minha === rodada.current) {
        investigacoes.current.clear();
        setPainel(resposta.data);
      }
    } catch (error: any) {
      if (minha !== rodada.current) return;
      const status = error?.response?.status;
      setPainel({
        disponivel: false,
        mensagem:
          status === 401
            ? 'Sua sessão expirou. Saia e entre de novo para ver o painel.'
            : status === 429
              ? 'Muitas consultas em pouco tempo. Aguarde um minuto e atualize.'
              : 'Não foi possível montar o painel agora.',
      });
    } finally {
      if (minha === rodada.current) setCarregando(false);
    }
  }, [user?.id, empresaCodigo, filialId, regiao, periodoEscolhido]);

  // Trocar de empresa, filial ou região mostra o carregando, não o relatório anterior.
  useEffect(() => {
    setPainel(null);
    setGaveta(null);
    setCarregando(false);
    carregar();
  }, [carregar]);

  const investigar = useCallback(
    (p: PedidoDeInvestigacao): Promise<Investigacao> => {
      const chave = `${p.analise}|${p.periodo.inicio}|${p.periodo.fim}|${p.colaborador_id || ''}`;
      const feita = investigacoes.current.get(chave);
      if (feita) return feita;
      const nova = axios
        .post(
          INVESTIGACAO_URL,
          {
            empresa_codigo: empresaCodigo,
            empresa_id: filialId || undefined,
            regiao: regiao || undefined,
            usuario_id: user?.id,
            analise: p.analise,
            inicio: p.periodo.inicio,
            fim: p.periodo.fim,
            colaborador_id: p.colaborador_id,
          },
          {
            headers: { Authorization: api.defaults.headers.common['Authorization'] },
            timeout: 60000,
          },
        )
        .then((r) => r.data as Investigacao)
        .catch((error: any) => {
          investigacoes.current.delete(chave); // a próxima tentativa consulta de novo
          const status = error?.response?.status;
          return {
            disponivel: false,
            mensagem:
              status === 429
                ? 'Muitas consultas em pouco tempo. Aguarde um minuto e tente de novo.'
                : status === 401
                  ? 'Sua sessão expirou. Saia e entre de novo.'
                  : 'Não foi possível investigar agora.',
          };
        });
      investigacoes.current.set(chave, nova);
      return nova;
    },
    [user?.id, empresaCodigo, filialId, regiao],
  );

  const raiz = (conteudo: React.ReactNode) => (
    <Box style={variaveisDoTema(ehClaro)} color="var(--pa-tinta)" fontSize="14px" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {conteudo}
    </Box>
  );

  if (!empresaCodigo) return raiz(<EscolhaDaEmpresa />);
  if (!painel) return raiz(<Carregando />);
  if (!painel.disponivel) {
    return raiz(
      <Box textAlign="center" pt="10">
        <Text color="var(--pa-tinta-2)" mb="4">
          {painel.mensagem}
        </Text>
        <Botao leftIcon={<FiRefreshCw />} isLoading={carregando} onClick={() => carregar(true)}>
          Tentar de novo
        </Botao>
      </Box>,
    );
  }

  const analises = painel.analises || [];
  const situacao = painel.situacao_de_hoje;
  const personalizada = analises.find((a) => a.analise === 'personalizado');
  const geradoEm = painel.gerado_em ? new Date(painel.gerado_em) : new Date();
  const ultima = painel.acompanhamento?.ultima_analise_programada;

  const empresa = empresas.find((e) => e.codigo === empresaCodigo);
  const filial = filiais.find((f) => f.id === filialId);
  const escopo = [
    empresa ? rotuloDaEmpresa(empresa) : empresaCodigo,
    filial ? `filial ${rotuloDaFilial(filial)}` : 'todas as filiais',
    regiao ? `região ${rotuloRegiao(regiao, filial?.codigo || empresaCodigo)}` : '',
    painel.so_os_seus_pedidos ? 'só os seus pedidos' : '',
  ].filter(Boolean);

  const abrirAlerta = (alerta: Alerta, analise?: Analise) => {
    const atual = analise?.periodo.atual;
    setGaveta({
      titulo: alerta.titulo,
      alerta,
      investigar:
        alerta.investigar && atual
          ? {
              analise: alerta.investigar.analise,
              periodo: atual,
              colaborador_id: alerta.investigar.colaborador_id,
              vendedor: alerta.investigar.vendedor,
            }
          : undefined,
    });
  };

  const abas: (DefinicaoDaAba & { analise?: Analise })[] = [
    // "Hoje" é só o dia; os alertas (de pedidos e clientes de qualquer data) vão em "Pendências".
    // Sem a aba "Hoje" (o dono, 04/11/2026): para ver o dia, a mesma data nas duas caixas.
    { nome: 'personalizado', titulo: 'Período escolhido', curto: 'Período', legenda: legendaDaAnalise(personalizada), resumo: personalizada?.resumo_dos_alertas, semHistorico: !!personalizada && !personalizada.disponivel, analise: personalizada },
    { nome: 'pendencias', titulo: 'Pendências', curto: 'Pendências', legenda: 'em aberto, no período escolhido', resumo: situacao?.resumo_dos_alertas },
    { nome: 'cirurgias', titulo: 'Cirurgias', curto: 'Cirurgias', legenda: painel.cirurgias?.periodo || 'por médico, convênio e tipo' },
  ];
  const ativa = abas.find((a) => a.nome === aba) || abas[0];

  return raiz(
    <Flex direction="column" sx={{ gap: '20px' }}>
      {/* O nome da página fica para o leitor de tela: o cabeçalho da página
          já diz onde se está, e os seletores dizem o que se consulta. */}
      <Box as="h1" position="absolute" w="1px" h="1px" overflow="hidden" sx={{ clip: 'rect(0 0 0 0)' }}>
        Inteligência comercial · {escopo.join(' · ')}
      </Box>

      {/* ── As três visões, e de quando são os dados ── */}
      <Flex justify="space-between" align="center" wrap="wrap" sx={{ gap: '12px 16px' }}>
        <AbasDoPainel abas={abas} ativa={ativa.nome} onEscolher={setAba} />
        <Flex align="center" wrap="wrap" sx={{ gap: '8px 12px' }} fontSize="12px" color="var(--pa-tinta-3)">
          {painel.so_os_seus_pedidos && <Etiqueta>só os seus pedidos</Etiqueta>}
          <Flex
            align="center"
            sx={{ gap: '6px' }}
            title={[
              'Os pedidos são relidos do sistema a cada 5 minutos.',
              painel.acompanhamento
                ? ultima
                  ? `Análise programada: ${dataHora(ultima.fim)}${ultima.sucesso ? '' : ' (falhou)'}.`
                  : 'A análise programada ainda não rodou.'
                : '',
            ].join(' ')}
          >
            <FiClock aria-hidden="true" />
            Atualizado às {geradoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </Flex>
          <Botao leftIcon={<FiRefreshCw />} isLoading={carregando} onClick={() => carregar(true)}>
            Atualizar
          </Botao>
        </Flex>
      </Flex>

      <Flex
        as="form"
        onSubmit={(evento: React.FormEvent) => {
          evento.preventDefault();
          if (datasDoFormulario.inicio > datasDoFormulario.fim) {
            setErroPeriodo('A data final deve ser igual ou posterior à inicial.');
            return;
          }
          setErroPeriodo('');
          setAba('personalizado');
          setPeriodoEscolhido({ ...datasDoFormulario });
        }}
        align="end"
        wrap="wrap"
        sx={{ gap: '10px 12px' }}
        p="14px"
        border="1px solid var(--pa-linha)"
        borderRadius="12px"
        bg="var(--pa-superficie)"
      >
        <Box>
          <Text display="block" mb="4px" fontSize="12px" fontWeight={700} color="var(--pa-tinta)">
            Período atual
          </Text>
          <Flex wrap="wrap" sx={{ gap: '8px' }}>
            <Box>
              <Text as="label" htmlFor="painel-data-inicio" fontSize="11px" color="var(--pa-tinta-2)">De</Text>
              <Input
                id="painel-data-inicio"
                type="date"
                value={datasDoFormulario.inicio}
                onChange={(evento: React.ChangeEvent<HTMLInputElement>) =>
                  setDatasDoFormulario((anterior) => ({ ...anterior, inicio: evento.target.value }))
                }
                required
                mt="3px"
                maxW="170px"
                bg="var(--pa-superficie)"
                borderColor="var(--pa-linha)"
                color="var(--pa-tinta)"
              />
            </Box>
            <Box>
              <Text as="label" htmlFor="painel-data-fim" fontSize="11px" color="var(--pa-tinta-2)">Até</Text>
              <Input
                id="painel-data-fim"
                type="date"
                value={datasDoFormulario.fim}
                onChange={(evento: React.ChangeEvent<HTMLInputElement>) =>
                  setDatasDoFormulario((anterior) => ({ ...anterior, fim: evento.target.value }))
                }
                required
                mt="3px"
                maxW="170px"
                bg="var(--pa-superficie)"
                borderColor="var(--pa-linha)"
                color="var(--pa-tinta)"
              />
            </Box>
          </Flex>
        </Box>
        <Box>
          <Text display="block" mb="4px" fontSize="12px" fontWeight={700} color="var(--pa-tinta)">
            Comparado com · período anterior
          </Text>
          <Flex wrap="wrap" sx={{ gap: '8px' }}>
            <Box>
              <Text as="label" htmlFor="painel-data-anterior-inicio" fontSize="11px" color="var(--pa-tinta-2)">De</Text>
              <Input
                id="painel-data-anterior-inicio"
                type="date"
                value={comparacaoDoFormulario?.inicio || ''}
                isDisabled
                mt="3px"
                maxW="170px"
                bg="var(--pa-superficie-2)"
                borderColor="var(--pa-linha)"
                color="var(--pa-tinta-2)"
              />
            </Box>
            <Box>
              <Text as="label" htmlFor="painel-data-anterior-fim" fontSize="11px" color="var(--pa-tinta-2)">Até</Text>
              <Input
                id="painel-data-anterior-fim"
                type="date"
                value={comparacaoDoFormulario?.fim || ''}
                isDisabled
                mt="3px"
                maxW="170px"
                bg="var(--pa-superficie-2)"
                borderColor="var(--pa-linha)"
                color="var(--pa-tinta-2)"
              />
            </Box>
          </Flex>
        </Box>
        <Botao type="submit" isLoading={carregando}>
          Consultar período
        </Botao>
        <Text w="100%" m={0} fontSize="12px" color="var(--pa-tinta-3)">
          {comparacaoDoFormulario ? (
            <>No padrão, o mês atual é comparado com as mesmas datas do mês anterior. Hoje fica fora enquanto o dia não estiver completo.</>
          ) : (
            'Informe um intervalo válido para ver a comparação com o mês anterior.'
          )}
        </Text>
        {erroPeriodo && <Text role="alert" color="var(--pa-critico)" fontSize="12px">{erroPeriodo}</Text>}
      </Flex>

      {/* O que vale para as três visões fica fora delas, antes das abas. */}
      {!!painel.limitacoes_gerais?.length && (
        <Aviso tom="atencao">
          {painel.limitacoes_gerais.map((t) => (
            <div key={t}>{t}</div>
          ))}
        </Aviso>
      )}

      {/* Um painel por aba; só o da ativa tem conteúdo. */}
      {abas.map((a) => (
        <Box
          key={a.nome}
          id={idDoConteudo(a.nome)}
          role="tabpanel"
          aria-labelledby={idDaAba(a.nome)}
          hidden={a.nome !== ativa.nome}
          tabIndex={0}
          minW={0}
          pt="4px"
          _focusVisible={{ outline: '2px solid var(--pa-dado)', outlineOffset: '4px', borderRadius: '8px' }}
        >
          {a.nome === ativa.nome && a.nome === 'pendencias' && situacao && (
            <AbaPendencias situacao={situacao} periodo={periodoEscolhido} onAbrir={(al) => abrirAlerta(al)} />
          )}
          {a.nome === ativa.nome && a.nome === 'cirurgias' && (
            <GraficosDeCirurgias dados={painel.cirurgias} />
          )}
          {a.nome === ativa.nome && a.analise && (
            <AbaComparacao
              key={a.analise.analise}
              analise={a.analise}
              cores={cores}
              onAbrir={(al) => abrirAlerta(al, a.analise)}
              onInvestigar={(p, titulo) => setGaveta({ titulo, investigar: p })}
              onPerguntar={onPerguntar}
              regioes={tabelaRegioes(empresaCodigo)}
              regiao={regiao}
              onEscolherRegiao={escolherRegiao}
            />
          )}
        </Box>
      ))}

      <Gaveta conteudo={gaveta} ehClaro={ehClaro} onFechar={() => setGaveta(null)} onPerguntar={onPerguntar} investigar={investigar} />
    </Flex>,
  );
}
